import { useMemo } from 'react'
import { useOmniAuth } from '@omni/auth'
import { frontPageUrl, type FrontDoorDomainConfig, type FrontDoorRpc } from '@omni/front-door'
import { activeInstance } from '../instances'

// The customer front door in the shell (EDNA-CC docs/customer-front-door.md).
// Everything under src/front-door/ is only reached behind the build-time flag
// __FRONT_DOOR__ (vite.config.ts), so instances without frontDoor.domain
// never carry it.

const fd = activeInstance.frontDoor

/** This build's front door: the domain, the cookie domain and the apps. */
export const frontDoor: FrontDoorDomainConfig & { adminName?: string } = {
  domain: fd?.domain ?? '',
  cookieDomain: fd?.cookieDomain,
  apps: (fd?.apps ?? []).map((a) => ({ slug: a.slug, subdomain: a.subdomain, modules: a.modules })),
  adminName: fd?.adminName,
}

/**
 * The launcher layout (instance frontDoor.layout === 'launcher'): a slim top
 * bar instead of the Command Centre side menu, and the front page as a
 * launcher of the person's apps (launcher/LauncherChrome.tsx).
 */
export const LAUNCHER = fd?.layout === 'launcher'

/** The app this build is (VITE_FRONT_DOOR_APP), or undefined on the front-door site. */
export const FRONT_DOOR_APP: string | undefined = import.meta.env.VITE_FRONT_DOOR_APP || undefined

/** https://<domain>/ */
export const FRONT_PAGE_URL = frontDoor.domain ? frontPageUrl(frontDoor.domain) : '/'

/** Who to ask for access, in a sentence. */
export const ASK_FOR_ACCESS = frontDoor.adminName ?? 'the person who looks after your sign-in'

/** The tenant database's public schema, through the shell's one session. */
export function useTenantRpc(): FrontDoorRpc {
  const { getClient } = useOmniAuth()
  return useMemo(() => getClient('crm').schema('public') as unknown as FrontDoorRpc, [getClient])
}
